import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { participantAPI, contributionAPI } from '@/lib/api';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, BarChart, Bar
} from 'recharts';
import {
  ChartCard, ActivityItem, QuickAction, EmptyState, SkeletonDashboard
} from '@/components/dashboard/Primitives';
import {
  TotalBalanceCard, TraditionalBalanceCard, RothBalanceCard,
  IncomeProjectionCard, ParticipantBadge
} from '@/components/dashboard/BalanceCards';
import { IFTStatusCard } from '@/components/dashboard/IFTStatusCard';
import { AlertsPanel } from '@/components/dashboard/AlertsPanel';
import { NewsDigestWidget } from '@/components/dashboard/NewsDigestWidget';
import { RecommendedAllocationWidget } from '@/components/dashboard/RecommendedAllocationWidget';
import { DownloadPdfButton } from '@/components/DownloadPdfButton';
import {
  TrendingUp, List, RefreshCw, ShieldCheck, FileText,
  CheckCircle, AlertTriangle, Percent, Users, Banknote,
  FileUp, PieChart as PieChartIcon, LineChart
} from 'lucide-react';

const CHART_COLORS = ['#1B2A4A', '#2D5F8A', '#3B8C6E', '#8B6B3B', '#6B3B8C', '#3B6B8C'];

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const fmtFull = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const fmtDate = (s) => { if (!s) return '--'; return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); };

export const DashboardPage = () => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [profile, setProfile] = useState(null);
  const [income, setIncome] = useState(null);
  const [iftStatus, setIftStatus] = useState(null);
  const [tsp, setTsp] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Check participant
      try { await participantAPI.getMyInfo(); }
      catch (err) { if (err.response?.status === 404) { navigate('/onboarding'); return; } }

      // Parallel fetch all data
      const [balRes, profRes, incRes, iftRes, tspRes, contRes, holdRes] = await Promise.allSettled([
        api.get('/tsp/participant/balance'),
        api.get('/tsp/participant/profile'),
        api.get('/tsp/participant/income-projection'),
        api.get('/tsp/participant/ift-status'),
        api.get('/tsp/summary'),
        contributionAPI.getMyContributions(),
        api.get('/investments/portfolio'),
      ]);

      if (balRes.status === 'fulfilled') setBalance(balRes.value.data);
      if (profRes.status === 'fulfilled') setProfile(profRes.value.data);
      if (incRes.status === 'fulfilled') setIncome(incRes.value.data);
      if (iftRes.status === 'fulfilled') setIftStatus(iftRes.value.data);
      if (tspRes.status === 'fulfilled') setTsp(tspRes.value.data);
      if (contRes.status === 'fulfilled') setContributions(Array.isArray(contRes.value.data) ? contRes.value.data : []);
      if (holdRes.status === 'fulfilled') {
        const h = holdRes.value.data?.holdings || holdRes.value.data?.portfolio || [];
        setHoldings(Array.isArray(h) ? h : []);
      }
    } catch {
      setError('Unable to load account data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <SkeletonDashboard />;
  if (error) return <div className="panel p-5 border-red-200" data-testid="dashboard-error"><p className="text-sm text-red-600">{error}</p></div>;

  const ytd = tsp?.contributions_ytd || {};
  const irs = tsp?.irs_limits || {};
  const fers = tsp?.fers_match || {};
  const vest = tsp?.vesting || {};
  const ift = tsp?.ift || {};
  const rmd = tsp?.rmd || {};
  const acct = tsp?.account || {};

  const allocationData = holdings.map((h, i) => ({
    name: h.symbol || h.fund_id || `Fund ${i + 1}`,
    value: h.market_value || h.cost_basis || 0,
  })).filter(d => d.value > 0);

  const perfData = [];
  const sorted = [...contributions].sort((a, b) => new Date(a.pay_date || a.created_at) - new Date(b.pay_date || b.created_at));
  let run = (balance?.total || 0) - (ytd.employee_total || 0) * 0.8;
  sorted.forEach(c => { if (c.contribution_type?.startsWith('employee')) { run += c.amount || 0; perfData.push({ date: fmtDate(c.pay_date), balance: Math.round(run) }); } });
  perfData.push({ date: 'Current', balance: Math.round(balance?.total || 0) });

  const recentActivity = [...contributions]
    .sort((a, b) => new Date(b.pay_date || b.created_at) - new Date(a.pay_date || a.created_at))
    .slice(0, 8);

  const matchData = [
    { name: 'Auto 1%', amount: fers.agency_auto_1pct || 0, fill: '#6B7280' },
    { name: 'Match (3%)', amount: fers.match_tier1_100pct_of_3pct || 0, fill: '#1B2A4A' },
    { name: 'Match (2%)', amount: fers.match_tier2_50pct_of_2pct || 0, fill: '#2D5F8A' },
  ];

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      {/* Participant Badge + Download */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ParticipantBadge retirementSystem={profile?.retirement_system} name={profile?.name} />
        <DownloadPdfButton
          path="/exports/account-statement.pdf"
          filename="UniFed_AccountStatement.pdf"
          label="Download Account Statement"
          testId="download-account-statement-btn"
        />
      </div>

      {/* Alerts Panel */}
      <AlertsPanel />

      {/* Row 1: Balance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="balance-cards-row">
        <TotalBalanceCard total={balance?.total} monthlyChange={balance?.monthly_change} monthlyChangePct={balance?.monthly_change_pct} />
        <TraditionalBalanceCard amount={balance?.traditional} />
        <RothBalanceCard amount={balance?.roth} />
        <IncomeProjectionCard projection={income} />
      </div>

      {/* Row 2: IRS Limits + FERS Match */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* IRS Limits */}
        <div className="panel p-5" data-testid="irs-limits-card">
          <span className="overline mb-3 block">IRS Contribution Limits — {irs.year}</span>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-[#6B7280]">Elective Deferral ({fmtFull(irs.used)} of {fmtFull(irs.personal_limit)})</span>
                <span className="font-mono font-medium text-[#1B2A4A]">{irs.pct_used}%</span>
              </div>
              <div className="w-full h-3 bg-[#F0F1F3] rounded-sm overflow-hidden">
                <div className="h-full bg-[#1B2A4A] rounded-sm transition-all" style={{ width: `${Math.min(irs.pct_used || 0, 100)}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-[#9CA3AF] mt-1 font-mono">
                <span>Standard: $23,500</span>
                <span>Remaining: {fmtFull(irs.remaining)}</span>
              </div>
            </div>
            {irs.catchup_eligible && (
              <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2">
                <p className="text-xs text-blue-700 font-medium">
                  {irs.catchup_type === 'enhanced_60_63' ? 'SECURE 2.0 Enhanced Catch-Up: ' : 'Catch-Up (50+): '}
                  +{fmtFull(irs.catchup_amount)}
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Traditional YTD</span><span className="font-mono text-[#1B2A4A]">{fmtFull(ytd.employee_traditional)}</span></div>
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Roth YTD</span><span className="font-mono text-[#1B2A4A]">{fmtFull(ytd.employee_roth)}</span></div>
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Annual Additions Limit</span><span className="font-mono text-[#1B2A4A]">{fmtFull(irs.annual_additions_limit)}</span></div>
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Total All Sources</span><span className="font-mono text-[#1B2A4A]">{fmtFull(ytd.total_all)}</span></div>
            </div>
          </div>
        </div>

        {/* FERS Match */}
        <div className="panel p-5" data-testid="fers-match-card">
          <span className="overline mb-3 block">FERS Match — 5 U.S.C. § 8432(c)</span>
          <div className="h-36">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={matchData} layout="vertical" margin={{ top: 0, right: 10, left: 60, bottom: 0 }}>
                <XAxis type="number" tick={{ fill: '#9CA3AF', fontSize: 10, fontFamily: 'IBM Plex Mono' }} tickFormatter={v => `$${(v/1000).toFixed(1)}k`} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#6B7280', fontSize: 11 }} width={70} />
                <Tooltip contentStyle={{ background: '#FFF', border: '1px solid #E2E5E9', borderRadius: 4, fontFamily: 'IBM Plex Mono', fontSize: 12 }} formatter={v => [fmtFull(v), '']} />
                <Bar dataKey="amount" radius={[0, 2, 2, 0]}>
                  {matchData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 space-y-1.5 text-xs">
            <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Your Contribution Rate</span><span className="font-mono font-medium text-[#1B2A4A]">{fers.employee_pct?.toFixed(1)}%</span></div>
            <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Total Agency (Annual)</span><span className="font-mono font-medium text-[#1B2A4A]">{fmtFull(fers.total_agency_contribution)}</span></div>
            <div className="flex items-center gap-2 py-1">
              {fers.capturing_full_match ?
                <><CheckCircle size={14} className="text-emerald-600" /><span className="text-emerald-700 font-medium">Capturing full FERS match</span></> :
                <><AlertTriangle size={14} className="text-amber-500" /><span className="text-amber-700 font-medium">Contribute 5% to capture full match</span></>
              }
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Charts + Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <ChartCard title="Balance History">
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={perfData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs><linearGradient id="balGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1B2A4A" stopOpacity={0.15} /><stop offset="100%" stopColor="#1B2A4A" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="#F0F1F3" />
                  <XAxis dataKey="date" tick={{ fill: '#9CA3AF', fontSize: 10, fontFamily: 'IBM Plex Mono' }} axisLine={{ stroke: '#E2E5E9' }} tickLine={false} />
                  <YAxis tick={{ fill: '#9CA3AF', fontSize: 10, fontFamily: 'IBM Plex Mono' }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: '#FFF', border: '1px solid #E2E5E9', borderRadius: 4, fontFamily: 'IBM Plex Mono', fontSize: 12 }} formatter={v => [`$${v.toLocaleString()}`, 'Balance']} />
                  <Area type="monotone" dataKey="balance" stroke="#1B2A4A" strokeWidth={2} fill="url(#balGrad)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <ChartCard title="Fund Allocation">
            {allocationData.length > 0 ? (
              <>
                <div className="h-40 flex items-center">
                  <ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={allocationData} cx="50%" cy="50%" innerRadius={40} outerRadius={62} dataKey="value" stroke="#FFF" strokeWidth={2}>{allocationData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Pie><Tooltip contentStyle={{ background: '#FFF', border: '1px solid #E2E5E9', borderRadius: 4, fontFamily: 'IBM Plex Mono', fontSize: 12 }} formatter={v => [fmtFull(v), '']} /></PieChart></ResponsiveContainer>
                </div>
                <div className="space-y-1">{allocationData.map((d, i) => (<div key={d.name} className="flex items-center justify-between text-xs"><div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} /><span className="text-[#6B7280] font-mono">{d.name}</span></div><span className="text-[#1B2A4A] font-mono font-medium">{fmtFull(d.value)}</span></div>))}</div>
              </>
            ) : <EmptyState icon={TrendingUp} title="No holdings" />}
          </ChartCard>
          <div className="grid grid-cols-1 gap-4">
            <IFTStatusCard used={iftStatus?.used} limit={iftStatus?.limit} resetDate={iftStatus?.reset_date} />
            <div className="panel p-4" data-testid="rmd-card"><span className="overline block mb-1">RMD Status</span><div className="font-mono text-xl font-semibold text-[#1B2A4A]">{rmd.years_until_rmd} yrs</div><span className="text-[10px] text-[#9CA3AF]">Age {rmd.current_age} → {rmd.rmd_age}</span></div>
          </div>
        </div>
      </div>

      {/* Row 4: Activity + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 panel p-5">
          <span className="overline mb-3 block">Recent Activity</span>
          {recentActivity.length > 0 ? (
            <div data-testid="activity-feed">{recentActivity.map((c, i) => {
              const labels = { 'employee_pretax': 'Employee Traditional', 'employee_roth': 'Employee Roth', 'employer_match': 'Agency Match', 'agency_auto': 'Agency Auto 1%' };
              return <ActivityItem key={i} description={labels[c.contribution_type] || c.contribution_type?.replace(/_/g, ' ')?.toUpperCase()} amount={fmtFull(c.amount)} date={fmtDate(c.pay_date || c.created_at)} status={c.status} />;
            })}</div>
          ) : <EmptyState icon={FileText} title="No recent activity" />}
        </div>
        <div className="space-y-4">
          <div className="panel p-5" data-testid="quick-actions-grid">
            <span className="overline mb-3 block">Quick Actions</span>
            <div className="grid grid-cols-2 gap-2">
              <QuickAction icon={Percent} label="Change Contribution %" onClick={() => navigate('/contributions')} />
              <QuickAction icon={Users} label="Add or Update Beneficiary" onClick={() => navigate('/beneficiaries')} />
              <QuickAction icon={Banknote} label="Request a Loan" onClick={() => navigate('/loans')} />
              <QuickAction icon={FileUp} label="Upload Documents" onClick={() => navigate('/documents')} />
              <QuickAction icon={PieChartIcon} label="Rebalance Fund Allocation" onClick={() => navigate('/fund-allocation')} />
              <QuickAction icon={LineChart} label="View Retirement Projection" onClick={() => navigate('/retirement-projection')} />
            </div>
          </div>
          <RecommendedAllocationWidget
            dob={profile?.date_of_birth}
            currentAlloc={(() => {
              // Map holdings -> {a,b,c,d,e} percentages based on fund symbol
              const map = { G: 'a', F: 'b', C: 'c', S: 'd', I: 'e' };
              const total = holdings.reduce((s, h) => s + (h.market_value || 0), 0) || 1;
              const out = { a: 0, b: 0, c: 0, d: 0, e: 0 };
              holdings.forEach((h) => {
                const sym = (h.symbol || h.fund_id || '').toString().toUpperCase();
                const key = map[sym] || map[sym[0]];
                if (key) out[key] += Math.round(((h.market_value || 0) / total) * 100);
              });
              return out;
            })()}
          />
          <NewsDigestWidget heldFunds={holdings.map(h => h.symbol || h.fund_id)} />
          <div className="panel p-5">
            <span className="overline mb-2 block">Account Details</span>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Account #</span><span className="font-mono font-medium text-[#1B2A4A]" data-testid="account-number">{acct.account_number || '--'}</span></div>
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Type</span><span className="text-[#1B2A4A]">UniFed Core</span></div>
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Salary</span><span className="font-mono text-[#1B2A4A]">{fmtFull(acct.annual_salary)}</span></div>
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Vesting</span><span className="text-[#1B2A4A]">{vest.vested ? 'Fully Vested' : `${vest.vesting_pct}%`}</span></div>
              <div className="flex justify-between py-1"><span className="text-[#9CA3AF]">Status</span><span className={acct.status === 'active' ? 'status-active' : 'status-pending'}>{acct.status?.toUpperCase()}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
